import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  Bell,
  Save,
  Trash2,
  AlertTriangle,
  Calendar,
  Tag,
  Building,
  Send,
  Clock,
} from "lucide-react";
import ChatIcon from "../../../components/Common/ChatIcon";
import { fetchClients } from "../../../features/onboarding/onboardingSlice";

const NOTIFICATION_TYPES = [
  { value: "feature", label: "Feature" },
  { value: "maintenance", label: "Maintenance" },
  { value: "security", label: "Security" },
  { value: "info", label: "Information" },
  { value: "update", label: "Update" },
];

const PRIORITY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
];

export default function ReleaseNotificationModal({
  mode = "add",
  notification = null,
  onClose,
  onSave,
  onDelete,
}) {
  const dispatch = useDispatch();
  const { clients: apiClients, clientsStatus } = useSelector(
    (state) => state.onboarding
  );

  // Map API clients to the expected format { id, name }
  const clients = useMemo(() => {
    return (apiClients || []).map((client) => ({
      id: client.clientUUID || client.id,
      name: client.clientName || client.name,
    }));
  }, [apiClients]);

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    priority: "medium",
    status: "draft",
    targetClientId: "all", // "all" or specific client ID
    publishedAt: "",
    expiresAt: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs for date inputs to trigger picker on field click
  const publishDateRef = useRef(null);
  const expiryDateRef = useRef(null);

  // Fetch clients on mount
  useEffect(() => {
    if (clientsStatus === "idle") {
      dispatch(fetchClients());
    }
  }, [dispatch, clientsStatus]);

  // Populate form for edit mode
  useEffect(() => {
    if (mode === "edit" && notification) {
      setFormData({
        title: notification.title || "",
        message: notification.message || "",
        type: notification.type || "info",
        priority: notification.priority || "medium",
        status: notification.status || "draft",
        targetClientId: notification.targetClientId || "all",
        publishedAt: notification.publishedAt
          ? new Date(notification.publishedAt).toISOString().slice(0, 16)
          : "",
        expiresAt: notification.expiresAt
          ? new Date(notification.expiresAt).toISOString().slice(0, 16)
          : "",
      });
    }
  }, [mode, notification]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 100) {
      newErrors.title = "Title must be 100 characters or less";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.length > 500) {
      newErrors.message = "Message must be 500 characters or less";
    }

    if (formData.status === "scheduled" && !formData.publishedAt) {
      newErrors.publishedAt = "Publish date is required for scheduled notifications";
    }

    if (formData.expiresAt && formData.publishedAt) {
      if (new Date(formData.expiresAt) <= new Date(formData.publishedAt)) {
        newErrors.expiresAt = "Expiry date must be after publish date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Get client name for display
      const targetClientName = formData.targetClientId === "all" 
        ? "All Clients" 
        : clients.find(c => c.id === formData.targetClientId)?.name || "Unknown Client";

      const submitData = {
        ...formData,
        targetClientName,
        publishedAt: formData.publishedAt ? new Date(formData.publishedAt).toISOString() : null,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
      };
      
      // If status is published and no publishedAt, set to now
      if (submitData.status === "published" && !submitData.publishedAt) {
        submitData.publishedAt = new Date().toISOString();
      }

      await onSave(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublishNow = async () => {
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Get client name for display
      const targetClientName = formData.targetClientId === "all" 
        ? "All Clients" 
        : clients.find(c => c.id === formData.targetClientId)?.name || "Unknown Client";

      const submitData = {
        ...formData,
        targetClientName,
        status: "published",
        publishedAt: new Date().toISOString(),
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
      };
      await onSave(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete confirmation modal
  if (mode === "delete") {
    return (
      <div className="modal-overlay">
        <div className="modal modal--delete">
          <div className="modal__header">
            <div className="modal__title modal__title--danger">
              <AlertTriangle size={20} />
              Delete Notification
            </div>
            <button className="modal__close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          <div className="modal__content">
            <div className="delete-confirmation">
              <div className="delete-confirmation__icon">
                <Trash2 size={32} />
              </div>
              <h3 className="delete-confirmation__title">
                Are you sure you want to delete this notification?
              </h3>
              <p className="delete-confirmation__message">
                <strong>"{notification?.title}"</strong>
              </p>
              <p className="delete-confirmation__warning">
                This action cannot be undone. The notification will be permanently removed.
              </p>
            </div>
          </div>

          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => {
                setIsSubmitting(true);
                onDelete();
              }}
              disabled={isSubmitting}
            >
              <Trash2 size={16} />
              {isSubmitting ? "Deleting..." : "Delete Notification"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal--large">
        <div className="modal__header">
          <div className="modal__title">
            <Bell size={20} />
            {mode === "add" ? "Create New Notification" : "Edit Notification"}
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal__content">
          <div className="form-grid">
            {/* Left Column - Content */}
            <div className="form-section">
              <h4 className="form-section__title">
                <ChatIcon size={16} />
                Notification Content
              </h4>

              <div className="form-field">
                <label className="form-field__label">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  className={`form-field__input ${errors.title ? "form-field__input--error" : ""}`}
                  placeholder="Enter notification title"
                  maxLength={100}
                />
                {errors.title && (
                  <span className="form-field__error">{errors.title}</span>
                )}
                <span className="form-field__hint">
                  {formData.title.length}/100 characters
                </span>
              </div>

              <div className="form-field">
                <label className="form-field__label">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  className={`form-field__textarea ${errors.message ? "form-field__input--error" : ""}`}
                  placeholder="Enter notification message..."
                  rows={5}
                  maxLength={500}
                />
                {errors.message && (
                  <span className="form-field__error">{errors.message}</span>
                )}
                <span className="form-field__hint">
                  {formData.message.length}/500 characters
                </span>
              </div>
            </div>

            {/* Right Column - Settings */}
            <div className="form-section">
              <h4 className="form-section__title">
                <Tag size={16} />
                Notification Settings
              </h4>

              <div className="form-row">
                <div className="form-field">
                  <label className="form-field__label">
                    <Tag size={14} />
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange("type", e.target.value)}
                    className="form-field__select"
                  >
                    {NOTIFICATION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label className="form-field__label">
                    <AlertTriangle size={14} />
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange("priority", e.target.value)}
                    className="form-field__select"
                  >
                    {PRIORITY_LEVELS.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label className="form-field__label">
                    <Clock size={14} />
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                    className="form-field__select"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label className="form-field__label">
                    <Building size={14} />
                    Target Client
                  </label>
                  <select
                    value={formData.targetClientId}
                    onChange={(e) => handleInputChange("targetClientId", e.target.value)}
                    className="form-field__select"
                    disabled={clientsStatus === "loading"}
                  >
                    <option value="all">All Clients</option>
                    {clientsStatus === "loading" ? (
                      <option value="" disabled>Loading clients...</option>
                    ) : (
                      clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <h4 className="form-section__title form-section__title--spaced">
                <Calendar size={16} />
                Schedule
              </h4>

              <div className="form-field">
                <label className="form-field__label">
                  Publish Date {formData.status === "scheduled" && "*"}
                </label>
                <input
                  ref={publishDateRef}
                  type="datetime-local"
                  value={formData.publishedAt}
                  onChange={(e) => handleInputChange("publishedAt", e.target.value)}
                  onClick={() => publishDateRef.current?.showPicker?.()}
                  className={`form-field__input form-field__input--date ${errors.publishedAt ? "form-field__input--error" : ""}`}
                />
                {errors.publishedAt && (
                  <span className="form-field__error">{errors.publishedAt}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-field__label">Expiry Date</label>
                <input
                  ref={expiryDateRef}
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) => handleInputChange("expiresAt", e.target.value)}
                  onClick={() => expiryDateRef.current?.showPicker?.()}
                  className={`form-field__input form-field__input--date ${errors.expiresAt ? "form-field__input--error" : ""}`}
                />
                {errors.expiresAt && (
                  <span className="form-field__error">{errors.expiresAt}</span>
                )}
                <span className="form-field__hint">
                  Leave empty for notifications that don't expire
                </span>
              </div>
            </div>
          </div>
        </form>

        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          
          {formData.status !== "published" && (
            <button
              type="button"
              className="btn btn--success"
              onClick={handlePublishNow}
              disabled={isSubmitting}
            >
              <Send size={16} />
              Publish Now
            </button>
          )}
          
          <button
            type="submit"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Save size={16} />
            {isSubmitting
              ? "Saving..."
              : mode === "add"
              ? "Create Notification"
              : "Save Changes"}
          </button>
        </div>

        {errors.form && <div className="form-error-alert">{errors.form}</div>}
      </div>
    </div>
  );
}

